/**
 * Сессия в браузере и в установленном PWA.
 *
 * Внутри Telegram сессия не нужна: там всегда под рукой initData, и токен
 * перевыпускается в любой момент. В вебе initData нет, а JWT живёт час —
 * поэтому здесь refresh-токен и его обмен.
 *
 * Формат хранения auth_token («Bearer …» в localStorage) намеренно оставлен
 * прежним: его читает интерцептор axios, который обслуживает и мини-апп.
 */

import axios from "axios";

const AUTH_TOKEN_KEY = "auth_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const USER_ID_KEY = "auth_user_id";

export type AuthProvider = "telegram" | "google";

export interface AuthSessionResponse {
  status: string;
  user_id: number;
  created: boolean;
  token: string;
  refresh_token: string;
  expires_in: number;
  needs_telegram?: boolean;
}

export interface LinkedIdentity {
  provider: AuthProvider;
  provider_uid: string | null;
  email: string | null;
  email_verified: boolean;
  display_name: string | null;
  avatar_url: string | null;
  linked_at: string;
  last_login_at: string | null;
}

/** Данные, которые отдаёт Telegram Login Widget. */
export interface TelegramWidgetPayload {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(^|;\\s*)" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[2]) : null;
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    // Приватный режим или запрещённое хранилище — работаем без сессии.
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* см. safeGet */
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* см. safeGet */
  }
}

export function getAuthToken(): string | null {
  const local = safeGet(AUTH_TOKEN_KEY);
  if (local) return local;
  const cookie = getCookie("wt_auth_token");
  if (cookie) {
    const val = `Bearer ${cookie}`;
    safeSet(AUTH_TOKEN_KEY, val);
    return val;
  }
  return null;
}

export function getRefreshToken(): string | null {
  const local = safeGet(REFRESH_TOKEN_KEY);
  if (local) return local;
  const cookie = getCookie("wt_refresh_token");
  if (cookie) {
    safeSet(REFRESH_TOKEN_KEY, cookie);
    return cookie;
  }
  return null;
}

export function getStoredUserId(): number | null {
  const raw = safeGet(USER_ID_KEY);
  if (raw) return Number(raw);
  const cookie = getCookie("wt_user_id");
  if (cookie) {
    safeSet(USER_ID_KEY, cookie);
    return Number(cookie);
  }
  return null;
}

export function hasWebSession(): boolean {
  return Boolean(getAuthToken() && getRefreshToken());
}

export function saveSession(session: AuthSessionResponse): void {
  safeSet(AUTH_TOKEN_KEY, `Bearer ${session.token}`);
  safeSet(REFRESH_TOKEN_KEY, session.refresh_token);
  safeSet(USER_ID_KEY, String(session.user_id));
}

export function clearSession(): void {
  safeRemove(AUTH_TOKEN_KEY);
  safeRemove(REFRESH_TOKEN_KEY);
  safeRemove(USER_ID_KEY);
  safeRemove("csrf_token");
  if (typeof document !== "undefined") {
    const domain = window.location.hostname.includes("webtransfer.app")
      ? "; domain=.webtransfer.app"
      : "";
    document.cookie = `wt_auth_token=; path=/${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    document.cookie = `wt_refresh_token=; path=/${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    document.cookie = `wt_user_id=; path=/${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }
}

/* ------------------------------------------------------------------ */
/* Вход                                                                */
/* ------------------------------------------------------------------ */

export async function loginWithTelegramWidget(
  payload: TelegramWidgetPayload,
  ref?: string
): Promise<AuthSessionResponse> {
  const { data } = await axios.post<AuthSessionResponse>(
    "/api/auth/telegram-widget.php",
    { ...payload, ref: ref || undefined }
  );
  saveSession(data);
  return data;
}

export async function loginWithGoogle(
  credential: string,
  ref?: string
): Promise<AuthSessionResponse> {
  const { data } = await axios.post<AuthSessionResponse>("/api/auth/google.php", {
    credential,
    ref: ref || undefined,
  });
  saveSession(data);
  return data;
}

/* ------------------------------------------------------------------ */
/* Привязка                                                            */
/* ------------------------------------------------------------------ */

/**
 * Привязка второго способа входа к текущему аккаунту.
 *
 * Это единственный путь объединения: автоматически по совпадению почты
 * аккаунты не склеиваются — Telegram почту не отдаёт, а совпадение адреса
 * не доказывает, что это тот же человек.
 */
export async function linkTelegram(
  payload: TelegramWidgetPayload
): Promise<{ identities: LinkedIdentity[]; bonus_granted: boolean }> {
  const { data } = await axios.post("/api/auth/telegram-widget.php", {
    ...payload,
    mode: "link",
  });
  return { identities: data.identities, bonus_granted: Boolean(data.bonus_granted) };
}

export async function linkGoogle(
  credential: string
): Promise<{ identities: LinkedIdentity[] }> {
  const { data } = await axios.post("/api/auth/google.php", {
    credential,
    mode: "link",
  });
  return { identities: data.identities };
}

export async function fetchIdentities(): Promise<{
  identities: LinkedIdentity[];
  payouts_blocked: boolean;
}> {
  const { data } = await axios.get("/api/auth/identities.php");
  return { identities: data.identities, payouts_blocked: Boolean(data.payouts_blocked) };
}

export async function unlinkIdentity(
  provider: AuthProvider
): Promise<{ identities: LinkedIdentity[]; payouts_blocked: boolean }> {
  const { data } = await axios.post("/api/auth/identities.php", {
    action: "unlink",
    provider,
  });
  return { identities: data.identities, payouts_blocked: Boolean(data.payouts_blocked) };
}

/* ------------------------------------------------------------------ */
/* Продление и выход                                                   */
/* ------------------------------------------------------------------ */

/**
 * Обменивает refresh-токен на новый JWT.
 *
 * Параллельные 401 не должны запускать обмен несколько раз: сервер отзывает
 * старый токен при первом же обмене, и второй запрос выглядел бы как кража —
 * с отзывом всех сессий. Поэтому обмен всегда один на всех ожидающих.
 */
let refreshInFlight: Promise<boolean> | null = null;

export function refreshSession(): Promise<boolean> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return Promise.resolve(false);
  }

  refreshInFlight = axios
    .post<AuthSessionResponse>("/api/auth/refresh.php", {
      refresh_token: refreshToken,
    })
    .then(({ data }) => {
      saveSession(data);
      return true;
    })
    .catch(() => {
      // Токен просрочен, отозван или украден — сессия закончилась.
      clearSession();
      return false;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

export async function logout(everywhere = false): Promise<void> {
  const refreshToken = getRefreshToken();

  try {
    await axios.post("/api/auth/logout.php", {
      refresh_token: refreshToken || undefined,
      all: everywhere || undefined,
    });
  } catch {
    // Выход не должен зависеть от доступности сети.
  }

  clearSession();
}
